import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import moment from 'moment';

@Component({
    selector: 'time-picker',
    templateUrl: './time-picker.component.html',
    styles: [],
    standalone: false
})
export class TimePickerComponent implements OnChanges {
    hours: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    minutes: number[];
    defaultMinutes: number[] = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
    @Input() show: boolean;
    /** Roster passes `[timeString, '']`; notifications pass a plain string. */
    @Input() dateTime: string | unknown[] | unknown;
    @Input() minutesArr: number[];

    @Output() cancel = new EventEmitter();
    @Output() update = new EventEmitter();

    preTime: string | undefined;
    hour: number;
    minute: number;
    ampm: string;

    /** Avoids re-parsing when `[dateTime]="[t,'']"` yields a new array each CD but the time string is unchanged. */
    private lastInputParseSignature: string | null = null;

    constructor() {}

    ngOnChanges(changes: SimpleChanges) {
        if (this.minutesArr?.length) {
            this.minutes = this.minutesArr;
        } else {
            this.minutes = this.defaultMinutes;
        }

        if (!changes['dateTime'] && !changes['minutesArr']) {
            return;
        }

        const timeStr = this.extractTimeString(this.dateTime);
        if (!timeStr || timeStr === 'Add Time') {
            this.lastInputParseSignature = null;
            this.hour = undefined as unknown as number;
            this.minute = undefined as unknown as number;
            this.ampm = undefined as unknown as string;
            this.preTime = undefined;
            return;
        }

        const trimmed = String(timeStr).trim();
        if (!trimmed) {
            this.lastInputParseSignature = null;
            this.hour = undefined as unknown as number;
            this.minute = undefined as unknown as number;
            this.ampm = undefined as unknown as string;
            this.preTime = undefined;
            return;
        }

        const signature = `${trimmed}::${this.minutes.join(',')}`;
        if (signature === this.lastInputParseSignature) {
            return;
        }

        this.preTime = trimmed;
        const parts = this.parseTo12h(trimmed, this.minutes);
        if (parts) {
            this.hour = parts.hour;
            this.minute = parts.minute;
            this.ampm = parts.ampm;
        } else {
            this.hour = undefined as unknown as number;
            this.minute = undefined as unknown as number;
            this.ampm = undefined as unknown as string;
        }

        this.lastInputParseSignature = signature;
    }

    /** Templates often use `[dateTime]="[value, '']"`; unwrap first element. */
    private extractTimeString(value: unknown): string | null {
        if (value == null) {
            return null;
        }
        if (Array.isArray(value)) {
            const first = value[0];
            if (first == null || first === '') {
                return null;
            }
            return String(first);
        }
        return String(value);
    }

    private snapToMinuteGrid(minute: number, grid: number[]): number {
        let best = grid[0];
        let bestDiff = Math.abs(minute - best);
        for (const m of grid) {
            const d = Math.abs(minute - m);
            if (d < bestDiff) {
                best = m;
                bestDiff = d;
            }
        }
        return best;
    }

    private parseTo12h(
        t: string,
        minuteGrid: number[]
    ): { hour: number; minute: number; ampm: string } | null {
        const formats = ['HH:mm:ss', 'HH:mm', 'h:mm A', 'hh:mm A'];
        for (const fmt of formats) {
            const m = moment.utc(t, fmt, true);
            if (m.isValid()) {
                return this.fromMoment24h(m, minuteGrid);
            }
        }
        const iso = moment.utc(t, moment.ISO_8601, true);
        if (iso.isValid()) {
            return this.fromMoment24h(iso, minuteGrid);
        }
        const loose = moment.utc(t);
        if (loose.isValid()) {
            return this.fromMoment24h(loose, minuteGrid);
        }
        return null;
    }

    private fromMoment24h(
        m: moment.Moment,
        minuteGrid: number[]
    ): { hour: number; minute: number; ampm: string } {
        const h24 = m.hour();
        const rawMin = m.minute();
        let h12 = h24 % 12;
        if (h12 === 0) {
            h12 = 12;
        }
        const ampm = h24 >= 12 ? 'PM' : 'AM';
        const minute = this.snapToMinuteGrid(rawMin, minuteGrid);
        return { hour: h12, minute, ampm };
    }

    cancelClick() {
        this.cancel.emit('');
    }

    /** True when hour, minute, and AM/PM are chosen (avoids emitting Invalid date from partial UI state). */
    canConfirmSelection(): boolean {
        if (this.hour == null || this.minute == null) {
            return false;
        }
        if (typeof this.hour !== 'number' || !Number.isFinite(this.hour)) {
            return false;
        }
        if (typeof this.minute !== 'number' || !Number.isFinite(this.minute)) {
            return false;
        }
        return this.ampm === 'AM' || this.ampm === 'PM';
    }

    updateClick() {
        if (!this.canConfirmSelection()) {
            return;
        }
        const minuteStr = String(this.minute).padStart(2, '0');
        const timeObj = `${this.hour}:${minuteStr} ${this.ampm}`;
        const parsed = moment.utc(timeObj, ['h:mm A', 'hh:mm A'], true);
        if (!parsed.isValid()) {
            return;
        }
        this.update.emit({
            value: parsed.format('HH:mm'),
            preTime: this.preTime
        });
    }
}
